import { Card, CardContent } from '@mui/material';
import { Form, Formik } from 'formik';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import * as Yup from 'yup';

import {
  FormikDropdownField,
  FormikTelField,
  FormikTextField,
} from '@components/FormikFields';
import RoundedButton from '@components/RoundedButton';

import { useCountryRegion } from '@hooks/useCountryRegion';

import type { AccountValues } from './types';

const messages = defineMessages({
  requiredText: {
    id: 'GLOBAL.requiredText',
    defaultMessage: 'Required',
  },
  name: {
    id: 'GLOBAL.name',
    defaultMessage: 'Name',
  },
  phone: {
    id: 'GLOBAL.phone',
    defaultMessage: 'Phone Number',
  },
  line1: {
    id: 'paymentInformationForm.line1',
    defaultMessage: 'Address 1',
  },
  line2: {
    id: 'paymentInformationForm.line2',
    defaultMessage: 'Address 2',
  },
  city: {
    id: 'GLOBAL.city',
    defaultMessage: 'City',
  },
  country: {
    id: 'GLOBAL.country',
    defaultMessage: 'Country',
  },
  postalCode: {
    id: 'GLOBAL.postalCode',
    defaultMessage: 'Postal/Zip Code',
  },
  region: {
    id: 'GLOBAL.region',
    defaultMessage: 'State/Province',
  },
  submit: {
    id: 'GLOBAL.next',
    defaultMessage: 'Next',
  },
  back: {
    id: 'GLOBAL.back',
    defaultMessage: 'Back',
  },
});

type FormValues = AccountValues;

interface AccountInformationProps {
  initialValues: FormValues;
  onSubmit?: (values: FormValues) => void;
  onBack?: () => void;
}

type AccountInformationFormProps = AccountInformationProps;

const AccountInformationForm: React.FC<AccountInformationFormProps> = ({
  initialValues,
  onSubmit,
  onBack,
}) => {
  const { formatMessage } = useIntl();
  const { countryOptions, getProvinceOptions } = useCountryRegion();

  const validationSchema: Yup.Schema<FormValues> = Yup.object().shape({
    name: Yup.string().required(formatMessage(messages.requiredText)),
    phone: Yup.string().required(formatMessage(messages.requiredText)),
    line1: Yup.string().required(formatMessage(messages.requiredText)),
    line2: Yup.string(),
    city: Yup.string().required(formatMessage(messages.requiredText)),
    province: Yup.string().required(formatMessage(messages.requiredText)),
    postalCode: Yup.string().required(formatMessage(messages.requiredText)),
    country: Yup.string().required(formatMessage(messages.requiredText)),
  });

  async function handleSubmit(values: AccountValues) {
    onSubmit?.({ ...values });
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {({ isSubmitting, setFieldValue, values }) => {
        const provinceOptions = getProvinceOptions(values.country);

        return (
          <Form>
            <FormikTextField
              label={formatMessage(messages.name)}
              name="name"
              required
            />

            <FormikTextField
              label={formatMessage(messages.line1)}
              name="line1"
              required
            />

            <FormikTextField
              label={formatMessage(messages.line2)}
              name="line2"
            />

            <FormikTextField
              label={formatMessage(messages.city)}
              name="city"
              required
            />

            <FormikDropdownField
              label={formatMessage(messages.country)}
              name="country"
              onChange={(value) => setFieldValue('country', value)}
              options={countryOptions}
              required
            />
            <FormikDropdownField
              label={formatMessage(messages.region)}
              name="province"
              onChange={(value) => setFieldValue('province', value)}
              options={provinceOptions}
              required
            />

            <FormikTextField
              label={formatMessage(messages.postalCode)}
              name="postalCode"
              required
            />

            <FormikTelField
              label={formatMessage(messages.phone)}
              name="phone"
              required
            />

            <RoundedButton disabled={isSubmitting} type="submit">
              {formatMessage(messages.submit)}
            </RoundedButton>
            {onBack && (
              <RoundedButton
                disabled={isSubmitting}
                onClick={onBack}
                type="button"
              >
                {formatMessage(messages.back)}
              </RoundedButton>
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

const AccountInformation: React.FC<AccountInformationProps> = ({
  initialValues,
  onSubmit,
  onBack,
}) => {
  return (
    <Card>
      <CardContent>
        <AccountInformationForm
          initialValues={initialValues}
          onSubmit={onSubmit}
          onBack={onBack}
        />
      </CardContent>
    </Card>
  );
};

export default AccountInformation;
